import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import attachIcon from '@/assets/common/box_paste.svg';
import wrapIcon from '@/assets/common/box_wraps_up.svg';
import printIcon from '@/assets/common/printer.svg';
import Button from '@/components/Button';
import Modal from '@/components/Modals/Modal';

interface OrderSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackingId: string;
  internalKey: string;
  isDraft: boolean;
  onPrintLabel: () => void;
  isNational?: boolean;
}

const OrderSuccessModal = (props: OrderSuccessModalProps) => {
  const { isOpen, onClose, trackingId, internalKey, isDraft, onPrintLabel, isNational } = props;
  const theme = useTheme();
  const { t } = useTranslation('orderSuccess');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', '¡Felicidades!')}
      maxWidth="md"
      scroll="paper"
      actions={
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            paddingBottom: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              minWidth: '240px',
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              fontWeight: 700,
              borderRadius: '30px',
              py: 1.25,
              fontSize: '0.95rem',
              boxShadow: '0 4px 14px rgba(106, 206, 59, 0.25)',
              textTransform: 'none',
              border: 'none',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: '0 6px 20px rgba(106, 206, 59, 0.35)',
                transform: 'translateY(-1px)',
                border: 'none',
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
            }}
          >
            {t('finishButton', 'Finalizar')}
          </Button>
        </Box>
      }
    >
      <Box sx={{ padding: { xxs: 1.5, sm: 3 } }}>
        <Typography
          variant="h5"
          sx={{
            textAlign: 'center',
            color: theme.palette.primary.main,
            fontWeight: 800,
            marginBottom: 1,
            letterSpacing: '-0.5px',
          }}
        >
          {t('orderConfirmed', 'Tu pedido fue confirmado')}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            color: theme.palette.text.secondary,
            marginBottom: 4,
            fontSize: '0.95rem',
          }}
        >
          {isNational ? (
            t('nationalIntro', 'Tu envío se procesará a través de un proveedor logístico externo')
          ) : (
            <>
              {t('prepareOrder', 'Prepara tu orden')}{' '}
              <strong style={{ color: theme.palette.text.primary }}>
                {t('beforePickup', 'antes de que pasemos a recolectarlo')}
              </strong>
            </>
          )}
        </Typography>

        {isNational ? (
          <Box
            sx={{
              backgroundColor: '#F9FAFB',
              borderLeft: '4px solid #85C440',
              padding: 2.5,
              borderRadius: '8px',
              marginBottom: 3.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', fontWeight: 700, color: 'text.primary', marginBottom: 1 }}
            >
              {t('nationalTitle', 'Tu envío es Nacional')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', color: theme.palette.text.secondary, fontSize: '0.85rem' }}
            >
              {t(
                'nationalMessage',
                'Este envío será gestionado por uno de nuestros proveedores logísticos aliados. Descarga tu guía y pégala en tu paquete; en breve te compartiremos los siguientes pasos para completarlo.',
              )}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Step Badges Row */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xxs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: { xxs: 3, sm: 4 },
                marginBottom: 4,
              }}
            >
              {/* Step 1 */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: '#F5FBEF',
                    border: '1px solid rgba(106, 206, 59, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 2,
                    boxShadow: '0 4px 10px rgba(106, 206, 59, 0.04)',
                  }}
                >
                  <Box
                    component="img"
                    src={wrapIcon}
                    alt={t('wrap', 'Envuelve')}
                    sx={{
                      height: 40,
                      width: 40,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                    marginBottom: 0.5,
                  }}
                >
                  {t('wrap', 'Envuelve')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    maxWidth: '160px',
                  }}
                >
                  {t('wrapCorrectly', 'tu orden correctamente')}
                </Typography>
              </Box>

              {/* Step 2 */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: '#F5FBEF',
                    border: '1px solid rgba(106, 206, 59, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 2,
                    boxShadow: '0 4px 10px rgba(106, 206, 59, 0.04)',
                  }}
                >
                  <Box
                    component="img"
                    src={printIcon}
                    alt={t('print', 'Imprime')}
                    sx={{
                      height: 40,
                      width: 40,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                    marginBottom: 0.5,
                  }}
                >
                  {t('print', 'Imprime')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    maxWidth: '160px',
                  }}
                >
                  {t('printGuide', 'la guía de tu envío')}
                </Typography>
              </Box>

              {/* Step 3 */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: '#F5FBEF',
                    border: '1px solid rgba(106, 206, 59, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 2,
                    boxShadow: '0 4px 10px rgba(106, 206, 59, 0.04)',
                  }}
                >
                  <Box
                    component="img"
                    src={attachIcon}
                    alt={t('attachGuide', 'Pega la guía')}
                    sx={{
                      height: 40,
                      width: 40,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                    marginBottom: 0.5,
                  }}
                >
                  {t('attachGuide', 'Pega la guía')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    maxWidth: '160px',
                  }}
                >
                  {t('visiblePlace', 'en un lugar visible')}
                </Typography>
              </Box>
            </Box>

            {/* Soft Notice Box */}
            <Box
              sx={{
                backgroundColor: '#F9FAFB',
                borderLeft: '4px solid #85C440',
                padding: 2,
                borderRadius: '8px',
                marginBottom: 3.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.8rem',
                  letterSpacing: '0.2px',
                  display: 'block',
                }}
              >
                {t(
                  'readyMessage',
                  'TENLO LISTO ANTES DE QUE NUESTRO REPARTIDOR PASE A RECOLECTARLO',
                )}
              </Typography>
            </Box>
          </>
        )}

        {/* Order Details Receipt Card */}
        <Box
          sx={{
            backgroundColor: '#FAFAFA',
            borderRadius: '12px',
            padding: 3,
            border: `1px solid rgba(0, 0, 0, 0.06)`,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.015)',
            marginBottom: 3.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.5px',
              display: 'block',
              marginBottom: 2,
            }}
          >
            {t('orderDetails', 'Detalles de tu orden')}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Tracking ID:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.primary.main,
                fontFamily: 'monospace',
                fontWeight: 800,
                fontSize: '1rem',
              }}
            >
              {trackingId}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.75, borderStyle: 'dashed', borderColor: 'rgba(0, 0, 0, 0.08)' }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Identificador de orden:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontFamily: 'monospace',
                fontWeight: 700,
                fontSize: '0.8rem',
              }}
            >
              {internalKey}
            </Typography>
          </Box>
        </Box>

        {/* Action / Warning Area */}
        {isDraft ? (
          <Box
            sx={{
              backgroundColor: '#FFFDF0',
              border: '1px solid #FFF0CD',
              borderRadius: '10px',
              padding: 2.25,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: '#8A6D3B',
                fontWeight: 700,
                lineHeight: 1.5,
              }}
            >
              {t(
                'draftOrderMessage',
                'Tu orden borrador fue creada. Programa la recolección para descargar la etiqueta.',
              )}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              padding: 3,
              backgroundColor: '#F8FCF3',
              border: '1px dashed rgba(106, 206, 59, 0.35)',
              borderRadius: '12px',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: 'text.primary',
                fontWeight: 700,
                fontSize: '0.9rem',
              }}
            >
              {t('downloadGuideInstruction', 'Haz clic abajo para descargar tu guía de envío')}
            </Typography>

            <IconButton
              onClick={onPrintLabel}
              sx={{
                width: 58,
                height: 58,
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                boxShadow: '0 4px 14px rgba(106, 206, 59, 0.3)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  transform: 'scale(1.05)',
                },
              }}
            >
              <CloudDownloadOutlinedIcon
                sx={{
                  fontSize: 30,
                  color: 'white',
                }}
              />
            </IconButton>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default OrderSuccessModal;
